import React, { useState, useEffect } from 'react';
import { Play, X, Clock, Star } from 'lucide-react';
import './NextEpisodeModal.css';

const NextEpisodeModal = ({ nextEpisode, onPlay, onCancel, autoPlayDelay = 10, isFullscreen = false }) => {
    const [countdown, setCountdown] = useState(autoPlayDelay);
    const [isMobile, setIsMobile] = useState(false);

    // Détecter si on est sur mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        // Countdown
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onPlay();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [onPlay]);

    if (!nextEpisode) return null;

    return (
        <div
            style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 12,
                pointerEvents: 'none',
                zIndex: 2147483647,
                background: isMobile ? 'rgba(0,0,0,0.65)' : 'transparent',
            }}
        >
            <div
                style={{
                    position: 'relative',
                    zIndex: 2147483647,
                    pointerEvents: 'auto',
                    width: '100%',
                    maxWidth: isMobile ? '90vw' : 440,
                    maxHeight: isMobile ? '70vh' : 'none',
                    background: 'var(--bg-modal)',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--r-xl)',
                    boxShadow: 'var(--shadow-xl)',
                    overflow: 'hidden',
                    animation: 'fadeUp 0.25s ease-out',
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderBottom: '1px solid var(--glass-border)',
                    background: 'var(--glass)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Clock style={{ width: 14, height: 14, color: 'var(--accent)', animation: 'glowPulse 2s infinite' }} />
                        <span style={{ color: 'var(--text-1)', fontWeight: 700, fontSize: '0.8125rem' }}>Épisode suivant</span>
                    </div>
                    <button
                        onClick={onCancel}
                        style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: 'var(--r-md)',
                            background: 'transparent', border: 'none',
                            color: 'var(--text-4)', cursor: 'pointer',
                            transition: 'background 150ms ease, color 150ms ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--glass-hover)'; e.currentTarget.style.color = 'var(--text-1)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-4)'; }}
                    >
                        <X style={{ width: 16, height: 16 }} />
                    </button>
                </div>

                {/* Content: thumbnail + info */}
                <div style={{ display: 'flex', gap: 12, padding: 12 }}>
                    {/* Thumbnail */}
                    <div style={{ position: 'relative', flexShrink: 0, width: isMobile ? 100 : 120, borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
                        <img
                            src={nextEpisode.episodeStill || nextEpisode.backdrop || nextEpisode.poster}
                            alt={nextEpisode.episodeTitle}
                            style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', display: 'block' }}
                        />
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(0,0,0,0.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Play style={{ width: 20, height: 20, color: '#fff', fill: '#fff' }} />
                        </div>
                    </div>

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{
                            display: 'inline-block',
                            padding: '1px 8px',
                            borderRadius: 'var(--r-full)',
                            background: 'rgba(232,183,90,0.25)',
                            border: '1px solid rgba(232,183,90,0.4)',
                            color: 'var(--accent-bright)',
                            fontSize: '0.6875rem', fontWeight: 700,
                            marginBottom: 6,
                        }}>
                            S{nextEpisode.season}E{nextEpisode.episode}
                        </span>
                        <h4 style={{
                            color: 'var(--text-1)', fontWeight: 700, fontSize: '0.75rem',
                            lineHeight: 1.35, marginBottom: 4,
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                            {nextEpisode.episodeTitle || `Épisode ${nextEpisode.episode}`}
                        </h4>
                        <p style={{
                            color: 'var(--text-4)', fontSize: '0.6875rem',
                            lineHeight: 1.45, marginBottom: 6,
                            overflow: 'hidden', display: '-webkit-box',
                            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                            {nextEpisode.episodeOverview || nextEpisode.overview}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-4)', fontSize: '0.625rem' }}>
                            <Clock style={{ width: 10, height: 10 }} />
                            <span>{nextEpisode.duration}</span>
                            {nextEpisode.rating && (
                                <>
                                    <span>·</span>
                                    <Star style={{ width: 10, height: 10, color: '#FCD34D', fill: '#FCD34D' }} />
                                    <span>{nextEpisode.rating}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 8, padding: '0 12px 12px' }}>
                    <button
                        onClick={onCancel}
                        className="btn-ghost"
                        style={{ flex: 1, padding: '8px 12px', fontSize: '0.8125rem', fontWeight: 500 }}
                    >
                        Arrêter
                    </button>
                    <button
                        onClick={onPlay}
                        className="btn-primary"
                        style={{ flex: 1, padding: '8px 12px', fontSize: '0.8125rem', fontWeight: 700, gap: 6 }}
                    >
                        <Play style={{ width: 13, height: 13, fill: 'currentColor' }} />
                        Regarder ({countdown}s)
                    </button>
                </div>

                {/* Countdown progress bar */}
                <div style={{ height: 3, background: 'var(--glass)' }}>
                    <div
                        style={{
                            height: '100%',
                            background: 'linear-gradient(90deg, var(--accent), var(--pink))',
                            width: `${(countdown / autoPlayDelay) * 100}%`,
                            transition: 'width 1s linear',
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default NextEpisodeModal;